import React, { useMemo, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box, Typography, Paper, Tabs, Tab } from '@mui/material';
import { sortAdminLanguages } from '../utils/languageOrder';
import { tinymceBaseInit, tinymceEditorSetup } from '../utils/tinymceEditorConfig';

interface MarkdownEditorMultilingualProps {
  value: { [key: string]: string };
  onChange: (value: { [key: string]: string }) => void;
  availableLanguages: string[];
  label?: string;
  placeholder?: string;
  height?: number;
}

const MarkdownEditorMultilingual: React.FC<MarkdownEditorMultilingualProps> = ({
  value,
  onChange,
  availableLanguages,
  label = 'Contenu',
  placeholder = 'Saisissez votre contenu (HTML)...',
  height = 400
}) => {
  const sortedLanguages = useMemo(
    () => sortAdminLanguages(availableLanguages),
    [availableLanguages]
  );
  const [selectedLang, setSelectedLang] = useState(sortedLanguages[0] || 'en');
  const hasUserSelectedLangRef = React.useRef(false);

  React.useEffect(() => {
    if (!sortedLanguages.includes(selectedLang)) {
      setSelectedLang(sortedLanguages[0] || 'en');
    }
  }, [sortedLanguages, selectedLang]);

  React.useEffect(() => {
    if (hasUserSelectedLangRef.current) return;
    const nextDefault = sortedLanguages[0] || 'en';
    if (nextDefault && selectedLang !== nextDefault) {
      setSelectedLang(nextDefault);
    }
  }, [sortedLanguages, selectedLang]);

  // S'assurer que toutes les langues ont une valeur (même vide)
  const safeValue = useMemo(() => {
    const result = { ...value };
    sortedLanguages.forEach(lang => {
      if (result[lang] === undefined) {
        result[lang] = '';
      }
    });
    return result;
  }, [value, sortedLanguages]);

  const handleMarkdownChange = (newValue?: string) => {
    const updatedValue = newValue || '';
    
    onChange({
      ...safeValue,
      [selectedLang]: updatedValue
    });
  };

  const editorInit = useMemo(
    () => ({
      ...tinymceBaseInit,
      height,
      placeholder,
      setup: tinymceEditorSetup,
    }),
    [height, placeholder]
  );

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography
          variant="subtitle2"
          fontWeight="700"
          sx={{ mt: 2, mb: 0.5, fontSize: '0.95rem' }}
        >
          {label}
        </Typography>
      )}
      
      {/* Onglets de langues */}
      <Tabs
        value={selectedLang}
        onChange={(_, newValue) => {
          hasUserSelectedLangRef.current = true;
          setSelectedLang(newValue);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: '36px' }}
      >
        {sortedLanguages.map((lang) => (
          <Tab
            key={lang}
            value={lang}
            label={lang.toUpperCase()}
            sx={{
              minHeight: '36px',
              py: 0.5,
              fontSize: '0.875rem'
            }}
          />
        ))}
      </Tabs>

      {/* Éditeur HTML (TinyMCE) */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          value={safeValue[selectedLang] || ''}
          init={{
            ...(editorInit as any),
            // Self-hosted mode: all plugin/skin assets are loaded from /tinymce
            base_url: '/tinymce'
          }}
          onEditorChange={(content) => handleMarkdownChange(content)}
        />
      </Paper>
    </Box>
  );
};

export default MarkdownEditorMultilingual;

